// pages/sections/ClientContactElSigner.js
// "Different Signer" pieces for Client Contact:
//   • ElSignerInfoModal — reference definitions of Signer vs Primary Contact,
//                         opened from the column's info icon.
//   • ElSignerFormModal — a complete, separate, OPTIONAL signer-contact form
//                         (name + full contact fields). Nothing is required and
//                         it never gates Mark Reviewed; it just records the
//                         signer when they're not the primary contact. Saved as
//                         a JSON client attribute.

import { useState } from 'react';
import Modal from '../../components/Modal';

export function ElSignerInfoModal({ open, onClose }) {
  return (
    <Modal open={open} onClose={onClose} title="Signer vs Primary Contact">
      <div className="cc-elsigner-info">
        <p className="cc-elsigner-info-note">
          <strong>When to use:</strong> Use the <strong>Different Signer</strong> button for a client
          when the signer is <em>not</em> the day-to-day primary contact. Record their details there.
          Every field is optional — fill in whatever you have. When the signer and the primary
          contact are the same person, leave it blank.
        </p>

        <h4>Signer Email</h4>
        <p>
          The email address of the individual authorized to execute agreements on behalf of the
          client. This is the person to whom binding documents are addressed for signature. For
          business or entity clients, the signer is often a principal,
          officer, board member, or other authorized representative — someone with legal authority to
          bind the entity — who may not be the day-to-day point of contact.
        </p>
        <p>For individual tax clients, signer email rules depend on filing status:</p>
        <ul>
          <li>
            <strong>Married Filing Jointly:</strong> Both the taxpayer's signer email and the
            spouse's signer email are required. Because both parties are co-signers on a joint
            return, binding documents must be executed by — and therefore delivered to — each spouse
            independently.
          </li>
        </ul>
        <p className="cc-elsigner-info-note">
          <strong>Note:</strong> When the primary contact and signer are the same person, a separate
          signer entry is not required — the primary contact email is assumed to serve both roles
          unless a distinction is explicitly recorded.
        </p>

        <h4>Primary Contact Email</h4>
        <p>
          The email address of the individual responsible for day-to-day coordination on an
          engagement. This is the person your staff communicates with to request documents, schedule
          fieldwork, answer questions, and relay status updates. For individual clients, this is
          typically the client themselves. For business or entity clients, this is usually a
          staff-level contact — an office manager, controller, or designated liaison — who may not
          have authority to execute agreements on behalf of the entity.
        </p>
      </div>
    </Modal>
  );
}

// Field set for the signer form (all optional).
const EL_FIELDS = [
  { key: 'firstName',   label: 'First Name' },
  { key: 'lastName',    label: 'Last Name' },
  { key: 'address',     label: 'Address' },
  { key: 'city',        label: 'City' },
  { key: 'state',       label: 'State' },
  { key: 'postCode',    label: 'Postal Code' },
  { key: 'email',       label: 'Email' },
  { key: 'phone',       label: 'Phone' },
  { key: 'spouseEmail', label: 'Spouse Email' },
];

// Complete signer form. Mounted only while open (fresh state per open), so the
// initializer seeds from the client's existing record without a poll resetting
// mid-edit. `onSave(record|null)` — a record with no filled fields saves as null
// (clears). `onClear` is a convenience for the Clear button (= onSave(null)).
export function ElSignerFormModal({ row, readOnly, onClose, onSave }) {
  const existing = (row && row.elSignerRecord) || null;
  const [rec, setRec] = useState(() => {
    const init = {};
    for (const f of EL_FIELDS) init[f.key] = (existing && existing[f.key]) || '';
    return init;
  });

  const clientLabel = row ? `${row.clientCode || ''} ${row.clientName || ''}`.trim() : '';
  const setField = (k, v) => setRec(prev => ({ ...prev, [k]: v }));

  function collect() {
    const clean = {};
    for (const f of EL_FIELDS) {
      const v = String(rec[f.key] || '').trim();
      if (v) clean[f.key] = v;
    }
    return Object.keys(clean).length ? clean : null;   // nothing filled → clears
  }

  // Optimistic: the page patches the row and POSTs in the background, so the form closes at once.
  // A refusal puts the old signer back and toasts the error (handled by the page).
  function save(record) {
    onSave(record);   // starts synchronously: the row is patched before the modal goes
    onClose();
  }

  return (
    <Modal open onClose={onClose} title="Different Signer">
      <div className="cc-elsigner-form">
        <p className="cc-elsigner-form-help">
          Record the signer for {clientLabel ? <strong>{clientLabel}</strong> : 'this client'} when
          they differ from the primary contact. All fields are optional.
        </p>
        <div className="cc-elsigner-grid">
          {EL_FIELDS.map(f => (
            <label key={f.key} className="cc-elsigner-grid-item">
              <span className="cc-elsigner-grid-label">{f.label}</span>
              <input
                className="cc-elsigner-grid-input"
                value={rec[f.key]}
                disabled={readOnly}
                onChange={e => setField(f.key, e.target.value)}
              />
            </label>
          ))}
        </div>
        <div className="cc-elsigner-form-actions">
          <button type="button" className="cc-create-btn cc-create-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          {!readOnly && existing && (
            <button type="button" className="cc-create-btn cc-create-btn--ghost" onClick={() => save(null)}>
              Clear signer
            </button>
          )}
          {!readOnly && (
            <button type="button" className="cc-create-btn" onClick={() => save(collect())}>
              Save signer
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
